import React from 'react';
import FlipMove from 'react-flip-move';
import { useSubscription } from '@meraki-internal/state';
import { Button } from '../components/button/Button.tsx';
import { Page } from '../components/page/Page.tsx';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { DrawerLabel } from '../components/drawer/DrawerLabel.tsx';
import { IonAccordion, IonAccordionGroup, IonItem, IonLabel, IonList } from '@ionic/react';
import { useAsyncEffect } from '../support/hooks/useAsyncEffect.tsx';
import { AircraftResponse, AircraftStatus } from './FleetManagmentClient.ts';
import { FleetManagementState } from './FleetManagementState.ts';
import { AircraftCard } from './AircraftCard.tsx';
import { HistoryViewModel } from '../app/HistoryViewModel.ts';
import { AIRCRAFT_STATUS_LABELS } from './labels.ts';
import { OperatorState } from '../profile/OperatorState.ts';
import { Refresher } from '../components/refresher/Refresher.tsx';
import { usePageView } from '../support/tracking/usePageView.tsx';

const splitFleet = (fleet: AircraftResponse[]) => {
    return fleet.reduce((map: {[status in AircraftStatus]: AircraftResponse[]}, a) => {
        map[a.status].push(a);
        return map;
    }, { active: [], grounded: [], archived: [] });
};

export const FleetManagementPage: React.FC = () => {
    usePageView('Equipment');

    const state = useInstance(FleetManagementState);
    useSubscription(() => state);

    const operatorState = useInstance(OperatorState);
    useSubscription(() => operatorState);

    const history = useInstance(HistoryViewModel);

    useAsyncEffect(async () => {
        await state.refresh();
    }, [state]);

    const statusGroupings = splitFleet(state.fleet);

    const onAdd = async () => {
        // guest must sign up before creating an aircraft
        if (!operatorState.ensureLoggedIn({ from: 'New Equipment', returnTo: '/fleet' })) {
            return;
        }

        state.startAddingAircraft();
    };

    const gotoDetailPage = (aircraft: AircraftResponse) => {
        history.push(`/fleet/${aircraft.aircraftId}`);
    };

    return (
        <Page title="Equipment" >

            <Refresher onRefresh={state.refresh} />

            {!state.hasAnyAircraft() ? (
                <div style={{margin: 30, textAlign: 'center'}}>
                    <h3>No Drones Yet!</h3>
                    <div style={{maxWidth: 300, margin: '50px auto 80px'}}>
                        <img src="./assets/images/fleet/no-drones.png" alt="No drones" />
                    </div>
                    <div style={{margin: 20}}>
                        <Button dataType="add-aircraft" expand="block" onClick={onAdd}>Add Your First Drone!</Button>
                    </div>
                </div>
            ) : (
                <div style={{margin: 20}}>
                    <Button dataType="add-aircraft" expand="block" onClick={onAdd}>New Drone</Button>
                </div>
            )}

            {statusGroupings.active.length > 0 &&
                <div style={{margin: '15px 20px -8px'}}>
                    <DrawerLabel text={AIRCRAFT_STATUS_LABELS.active} />
                </div>
            }

            <FlipMove duration={750} delay={1500} enterAnimation="none">
                {statusGroupings.active.sort(state.activeSort).map(aircraft => (
                    <div key={aircraft.aircraftId} style={{position: 'relative'}}>
                        <AircraftCard
                            aircraft={aircraft}
                            onClick={() => gotoDetailPage(aircraft)}
                        />
                    </div>
                ))}
            </FlipMove>

            <IonAccordionGroup style={{marginBottom: 20}}>
                {(['grounded', 'archived'] as AircraftStatus[]).map(status => (
                    <div key={status}>
                        {statusGroupings[status].length > 0 && (
                            <IonAccordion value={status}>
                                <IonItem slot="header">
                                    <IonLabel>{AIRCRAFT_STATUS_LABELS[status]}</IonLabel>
                                </IonItem>
                                <IonList slot="content">
                                    {statusGroupings[status].sort(state.alphaSort).map(aircraft => (
                                        <AircraftCard
                                            key={aircraft.aircraftId}
                                            aircraft={aircraft}
                                            onClick={() => gotoDetailPage(aircraft)}
                                        />
                                    ))}
                                </IonList>
                            </IonAccordion>
                        )}
                    </div>
                ))}
            </IonAccordionGroup>

        </Page>
    );
};
